import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Grid } from "@mui/material";
import Button from "@mui/material/Button";
import { v4 as uuid } from "uuid";

import medicationFormList from "../../assets/medicationFormList";
import { OmniStringToDate } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const PatientMedicationDialog = props => {
  const {
    open,
    dialogTitle = "Patient Medication",
    dialogMessage = null,
    okMessage = "OK",
    cancelMessage = "Cancel",
    deleteMessage = "remove",
    maxWidth = "xs",
    fullWidth = true,
    allMedications,
    medication = "",
    rowCount = 5,
  } = props;

  const [showConfirm, setShowConfirm] = useState(false);

  const [originalMedicationName] = useState(medication);
  const [medicationName, setMedicationName] = useState(medication);
  const [medicationForm, setMedicationForm] = useState(
    medication === "" ? "" : allMedications[medication].form,
  );
  const [medicationRefillDate] = useState(
    medication === "" ? new Date() : OmniStringToDate(allMedications[medication].refillDate),
  );
  const [medicationDosage, setMedicationDosage] = useState(
    medication === "" ? "" : allMedications[medication].dosage,
  );
  const [medicationNote, setMedicationNote] = useState(
    medication === "" ? "" : allMedications[medication].note,
  );
  const [medicationTimes, setMedicationTimes] = useState(
    medication === "" ? "" : allMedications[medication].schedule.times,
  );
  const [medicationTimeUnit, setMedicationTimeUnit] = useState(
    medication === "" ? "" : allMedications[medication].schedule.timeUnit,
  );

  const onDeleteHandler = () => {
    if (originalMedicationName !== "") {
      delete allMedications[originalMedicationName];
    }
    props.onAccept(allMedications);
  };

  const onAcceptHandler = () => {
    if (medicationName !== originalMedicationName && originalMedicationName !== "") {
      delete allMedications[originalMedicationName];
    }
    allMedications[medicationName] = {
      form: medicationForm,
      refillDate: medicationRefillDate.toISOString(),
      dosage: medicationDosage,
      note: medicationNote,
      schedule: {
        times: medicationTimes,
        timeUnit: medicationTimeUnit,
      },
    };
    props.onAccept(allMedications);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        {dialogMessage && <DialogMessage>{dialogMessage}</DialogMessage>}
        <OmniTextField
          label="Medication Name *"
          type="textInput"
          name={uuid()} //prevents AutoComplete Suggestions
          variant="outlined"
          value={medicationName}
          onChange={event => {
            setMedicationName(event.target.value);
          }}
          autoFocus
          placeholder={"This can be found on your prescription bottle."}
        />

        <Grid container spacing={1} direction="row">
          <Grid item xs={4}>
            <OmniTextField
              label="Take"
              type="number"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={medicationTimes}
              onChange={event => {
                setMedicationTimes(event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <OmniTextField
              label="Form"
              select
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={medicationForm}
              onChange={event => {
                setMedicationForm(event.target.value);
              }}
            >
              {medicationFormList.map(medicationForm => (
                // Want to eventually use the FDA approved code
                <OmniMenuItem key={medicationForm.code} value={medicationForm.description}>
                  {medicationForm.description}
                </OmniMenuItem>
              ))}
            </OmniTextField>
          </Grid>
          <Grid item xs={3}>
            <OmniTextField
              label="When"
              // type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={medicationTimeUnit}
              onChange={event => {
                setMedicationTimeUnit(event.target.value);
              }}
            />
          </Grid>
        </Grid>

        <OmniTextField
          label="Dosage"
          type="textInput"
          name={uuid()} //prevents AutoComplete Suggestions
          variant="outlined"
          value={medicationDosage}
          onChange={event => {
            setMedicationDosage(event.target.value);
          }}
        />

        <OmniTextField
          label="Instructions"
          type="textInput"
          name={uuid()} //prevents AutoComplete Suggestions
          variant="outlined"
          value={medicationNote}
          rowCount={rowCount}
          multiline
          onChange={event => {
            setMedicationNote(event.target.value);
          }}
        />
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {originalMedicationName !== "" && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={medicationName == null || medicationName.length < 1}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to remove this medication?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default PatientMedicationDialog;
